/**
 * Completes a password reset from an emailed link.
 *
 * On success the server revokes every existing session
 * (revokeSessionsOnPasswordReset), so the user must sign in again — including
 * anyone who had stolen a session.
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isKnownNext, passwordSchema } from '@ideabrief/core';
import { authClient } from '../auth';
import { Button, Field, FormError, Logo, Notice, Wordmark } from '../components/ui';
import { AuthProgress } from '../components/AuthProgress';

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  // A reset requested mid-flow carries next= on its emailed link, so the
  // sign-in that follows returns the user to their brief instead of dumping
  // them on the dashboard with the draft stranded. Allowlist key only — it is
  // validated with isKnownNext and re-passed as a key, never used as a path.
  const nextParam = params.get('next');
  const signInPath = isKnownNext(nextParam)
    ? `/auth?mode=in&next=${nextParam}`
    : '/auth?mode=in';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [phase, setPhase] = useState<'idle' | 'pending' | 'done'>('idle');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    setFieldError(null);

    const result = passwordSchema.safeParse(password);
    if (!result.success) {
      setFieldError(result.error.issues[0]?.message ?? 'Check your password.');
      return;
    }
    if (password !== confirm) {
      setFieldError("Those passwords don't match.");
      return;
    }

    setPhase('pending');
    try {
      const { error } = await authClient.resetPassword({ newPassword: password, token });
      if (error) throw error;
      setPhase('done');
    } catch {
      setPhase('idle');
      setFormError(
        'That reset link has expired or already been used. Request a new one.',
      );
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header style={{ borderBottom: '1px solid var(--line2)' }}>
        <div
          style={{
            maxWidth: 1080,
            margin: '0 auto',
            padding: '14px 24px',
            display: 'flex',
            alignItems: 'center',
            gap: 9,
          }}
        >
          <Logo />
          <Wordmark />
        </div>
      </header>

      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 20px 90px',
        }}
      >
        <div style={{ width: '100%', maxWidth: 420 }}>
          {!token ? (
            <Notice tone="amber">
              That reset link is incomplete. Request a new one from the sign-in
              page.
            </Notice>
          ) : phase === 'done' ? (
            <>
              <Notice tone="green">
                <strong style={{ display: 'block', marginBottom: 6 }}>
                  Password updated.
                </strong>
                Any other devices signed into this account have been signed out.
              </Notice>
              <div style={{ marginTop: 18 }}>
                <Button full onClick={() => navigate(signInPath)}>
                  Sign in
                </Button>
              </div>
            </>
          ) : (
            <>
              <h1
                style={{
                  margin: 0,
                  fontSize: 29,
                  lineHeight: 1.16,
                  letterSpacing: '-.03em',
                  fontWeight: 600,
                }}
              >
                Choose a new password.
              </h1>
              <form
                onSubmit={handleSubmit}
                style={{
                  marginTop: 24,
                  border: '1px solid var(--line)',
                  borderRadius: 16,
                  background: 'var(--card)',
                  padding: 24,
                  boxShadow: 'var(--shadow-card)',
                }}
              >
                {phase === 'pending' ? (
                  <AuthProgress action="password" />
                ) : (
                  <>
                    {formError && <FormError>{formError}</FormError>}
                    <Field
                      label="New password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      error={fieldError}
                      hint="At least 12 characters. A short phrase beats a complicated word."
                      required
                    />
                    <Field
                      label="Confirm new password"
                      type="password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      autoComplete="new-password"
                      required
                    />
                    <Button type="submit" full>
                      Update password
                    </Button>
                  </>
                )}
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
